import { useRef, useState } from "react";
import { formatDate } from "../../api/dates";
import { useAddEvidence, type ChecksView } from "../../api/inspections";

/** The photos, videos and PDFs attached to the visit, each optionally tied to
 * the check it proves, and adding one. The photograph items above carry their
 * own cameras; this is for anything else -- a video, a scanned sheet, a photo
 * of something the list did not ask for. */
export default function EvidencePanel({ view, edit }: { view: ChecksView; edit: boolean }) {
  const attach = useAddEvidence(view.inspection.id);
  const [caption, setCaption] = useState("");
  const [checkCode, setCheckCode] = useState("");
  const input = useRef<HTMLInputElement>(null);
  return (
    <section className="evidence" aria-label="Evidence">
      <h4 className="check-group-title">
        Photos and files <span className="count-badge">{view.evidence.length}</span>
      </h4>
      <div className="evidence-grid">
        {view.evidence.map((e) => (
          <figure key={e.id} className="evidence-tile">
            {e.kind === "image" ? (
              <a href={e.url} target="_blank" rel="noreferrer">
                <img src={e.url} alt={e.caption ?? e.evidence_type} loading="lazy" />
              </a>
            ) : (
              <a className="evidence-file" href={e.url} target="_blank" rel="noreferrer">
                {e.kind === "video" ? "Video" : "PDF"}
              </a>
            )}
            <figcaption>
              <span>
                {e.check_code ? `${e.check_code} · ` : ""}
                {e.caption ?? e.evidence_type}
              </span>
              <span>{e.captured_at ? formatDate(e.captured_at) : ""}</span>
            </figcaption>
          </figure>
        ))}
        {edit ? (
          <button type="button" className="evidence-add" disabled={attach.isPending} onClick={() => input.current?.click()}>
            {attach.isPending ? "Uploading…" : "+ Photo, video or PDF"}
          </button>
        ) : null}
      </div>
      {!edit && view.evidence.length === 0 ? <p className="empty-state">Nothing attached.</p> : null}
      {edit ? (
        <details className="insp-more">
          <summary>Say what the next upload shows</summary>
          <div className="evidence-fields">
            <label className="field">
              Caption
              <input value={caption} onChange={(e) => setCaption(e.target.value)} />
            </label>
            <label className="field">
              For check
              <select value={checkCode} onChange={(e) => setCheckCode(e.target.value)}>
                <option value="">The visit as a whole</option>
                {view.sections
                  .flatMap((s) => s.items)
                  .map((i) => (
                    <option key={i.code} value={i.code}>
                      {i.code} {i.label}
                    </option>
                  ))}
              </select>
            </label>
          </div>
        </details>
      ) : null}
      {edit ? (
        <input
          ref={input}
          type="file"
          hidden
          accept={view.accepted_types.join(",")}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              attach.mutate({ valveId: view.active_valve_id, file, caption: caption.trim(), checkCode }, { onSuccess: () => setCaption("") });
            }
            e.target.value = "";
          }}
        />
      ) : null}
      {attach.isError ? <p className="notice bad">The file was not attached: {attach.error.message}</p> : null}
    </section>
  );
}
